import { useCallback, useEffect, useRef, useState } from "react"
import { useLazyGetOrdersByDateQuery } from "src/app/apis/baseApi"
import { Order } from "src/db/Order"

const PAGE_SIZE = 50

/**
 * Fetches all orders for a given date by paginating through the backend.
 * Keeps fetching until hasMore is false, then returns the full list.
 */
export const useAllOrders = (date: string) => {
  const [trigger] = useLazyGetOrdersByDateQuery()
  const [orders, setOrders] = useState<Order[]>([])
  const [isFetching, setIsFetching] = useState(false)
  const [error, setError] = useState<any>(null)
  const [total, setTotal] = useState(0)
  const abortRef = useRef(false)

  const fetchAll = useCallback(async () => {
    if (!date) return

    abortRef.current = false
    setIsFetching(true)
    setError(null)

    const accumulated: Order[] = []
    let offset = 0
    let hasMore = true

    try {
      while (hasMore && !abortRef.current) {
        const result = await trigger({
          date,
          limit: PAGE_SIZE,
          offset,
        }).unwrap()
        accumulated.push(...result.data)
        setTotal(result.total)
        hasMore = result.hasMore
        offset += PAGE_SIZE
      }

      if (!abortRef.current) {
        setOrders(accumulated)
      }
    } catch (err) {
      setError(err)
    } finally {
      setIsFetching(false)
    }
  }, [date, trigger])

  useEffect(() => {
    fetchAll()
    return () => {
      abortRef.current = true
    }
  }, [fetchAll])

  return {
    data: orders,
    isFetching,
    error,
    total,
    refetch: fetchAll,
  }
}
